import React from 'react';

import type { IdAccountDto } from '@/services/ws-crm/src/main/resources/rest-api';
import type { Role } from '@/services/ws-orders/src/main/resources/rest-api';
import { getAccounts } from '@/services/ws-orders/src/main/resources/rest-api';
import { useSubscribe } from '@/js/libs/observable';
import { useRequest } from '@/js/libs/use-request';

import { DEFAULT_DEBOUNCE_DELAY, DROP_DOWN_ENTITY_LIMIT } from 'src/constants';
import type { Option } from 'src/types';

type UseAccountsByRoleOptionsReturn = {
  options: Option<IdAccountDto>[];
  pending: boolean;
  total: number;
  searchFilter: string;
  setSearchFilter: (filter: string) => void;
};

type UseAccountsByRoleOptions = (role?: Role) => UseAccountsByRoleOptionsReturn;

export const useAccountsByRoleOptions: UseAccountsByRoleOptions = (role) => {
  const [searchFilter, setSearchFilter] = React.useState<string>('');

  const getAccountListRequest = useRequest({
    apiMethod: getAccounts,
    params: { prefix: searchFilter, role, limit: DROP_DOWN_ENTITY_LIMIT, page: 0 },
    debounce: DEFAULT_DEBOUNCE_DELAY,
  });

  const options = useSubscribe(
    getAccountListRequest.data$,
    (response) =>
      response?.data.items.map((item) => ({
        label: item?.displayName ?? '',
        value: item.id,
        active: item.active,
      })) ?? [],
  );
  const total = useSubscribe(getAccountListRequest.data$, (result) => result?.data?.total ?? 0);
  const pending = useSubscribe(getAccountListRequest.pending$);

  return React.useMemo<UseAccountsByRoleOptionsReturn>(
    () => ({
      options,
      pending,
      total,
      searchFilter,
      setSearchFilter,
    }),
    [options, pending, total, searchFilter],
  );
};
